import { Bug, X as CloseIcon, Image as ImageIcon, Lightbulb, Loader2, MessageSquarePlus } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { getHeaders } from "@/lib/api";
import type { FeedbackItem } from "@/lib/services";
import { feedbackService } from "@/lib/services";

const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed"] as const;
const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};
const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-500/20 text-blue-400",
  in_progress: "bg-yellow-500/20 text-yellow-400",
  resolved: "bg-green-500/20 text-green-400",
  closed: "bg-surface-tertiary/20 text-content-tertiary",
};

/** Fetches an auth-protected image and returns an object URL. */
function useAuthImage(url: string | null) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setSrc(null);
      return;
    }

    let revoke: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const headers = await getHeaders();
        headers.delete("Content-Type");
        const res = await fetch(url, { headers });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        revoke = objectUrl;
        setSrc(objectUrl);
      } catch {
        // silently fail
      }
    })();

    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [url]);

  return src;
}

function ScreenshotThumbnail({
  feedbackUuid,
  screenshot,
  onClick,
}: Readonly<{
  feedbackUuid: string;
  screenshot: FeedbackItem["screenshots"][number];
  onClick: () => void;
}>) {
  const url = feedbackService.screenshotUrl(feedbackUuid, screenshot.blobName);
  const src = useAuthImage(url);

  return (
    <button
      onClick={onClick}
      className="relative w-12 h-12 rounded-lg overflow-hidden border border-border-secondary hover:border-border-hover transition-colors shrink-0 bg-surface-secondary"
    >
      {src ? (
        <img src={src} alt={screenshot.originalFilename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-4 h-4 text-content-quaternary" />
        </div>
      )}
    </button>
  );
}

function ScreenshotLightbox({
  feedbackUuid,
  screenshots,
  initialIndex,
  onClose,
}: Readonly<{
  feedbackUuid: string;
  screenshots: FeedbackItem["screenshots"];
  initialIndex: number;
  onClose: () => void;
}>) {
  const [index, setIndex] = useState(initialIndex);
  const current = screenshots[index];
  const url = feedbackService.screenshotUrl(feedbackUuid, current.blobName);
  const src = useAuthImage(url);

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-xs"
        role="presentation"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      />{" "}
      {/* NOSONAR — decorative backdrop element */}
      <div className="relative max-w-4xl max-h-[85vh] flex flex-col items-center gap-4">
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10 p-2 bg-surface-secondary hover:bg-interactive-active rounded-full text-content-secondary hover:text-content-primary transition-colors"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
        {src ? (
          <img
            src={src}
            alt={current.originalFilename}
            className="max-h-[75vh] max-w-full rounded-lg border border-border-secondary object-contain"
          />
        ) : (
          <div className="w-96 h-64 flex items-center justify-center bg-surface-primary rounded-lg border border-border-secondary">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          </div>
        )}
        {screenshots.length > 1 && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
              className="px-3 py-1 bg-surface-secondary text-content-primary rounded-lg disabled:opacity-50 hover:bg-interactive-active transition-colors text-sm"
            >
              Prev
            </button>
            <span className="text-sm text-content-tertiary">
              {index + 1} / {screenshots.length}
            </span>
            <button
              onClick={() => setIndex((i) => Math.min(screenshots.length - 1, i + 1))}
              disabled={index === screenshots.length - 1}
              className="px-3 py-1 bg-surface-secondary text-content-primary rounded-lg disabled:opacity-50 hover:bg-interactive-active transition-colors text-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminFeedback() {
  const { showToast } = useToast();
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const pageSize = 20;

  // Detail / lightbox state
  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{
    feedbackUuid: string;
    screenshots: FeedbackItem["screenshots"];
    index: number;
  } | null>(null);

  // Inline status-update state
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const loadFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const response = await feedbackService.list({
        page,
        pageSize,
        category: filterCategory || undefined,
        status: filterStatus || undefined,
      });
      setItems(response.data || []);
      setTotal(response.meta?.total || 0);
    } catch (err) {
      console.error("Failed to load feedback:", err);
      showToast("Failed to load feedback", "error");
    } finally {
      setLoading(false);
    }
  }, [page, filterCategory, filterStatus, showToast]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const handleStartEdit = (item: FeedbackItem) => {
    setEditingUuid(item.uuid);
    setEditStatus(item.status);
    setEditNotes(item.adminNotes || "");
    // Ensure the row is expanded so the inline edit form is visible
    setExpandedUuid(item.uuid);
  };

  const handleCancelEdit = () => {
    setEditingUuid(null);
    setEditStatus("");
    setEditNotes("");
  };

  const handleSaveStatus = async (uuid: string) => {
    try {
      setSaving(true);
      await feedbackService.updateStatus(uuid, {
        status: editStatus,
        admin_notes: editNotes || null,
      });
      showToast("Feedback updated", "success");
      handleCancelEdit();
      await loadFeedback();
    } catch (err) {
      console.error("Failed to update feedback:", err);
      showToast("Failed to update feedback", "error");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={filterCategory}
          onChange={(e) => {
            setFilterCategory(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
        >
          <option value="">All Categories</option>
          <option value="bug">Bug Reports</option>
          <option value="feature">Feature Requests</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>

        <div className="ml-auto text-sm text-content-tertiary">
          {total} item{total === 1 ? "" : "s"}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-primary">
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Title
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Submitted By
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {items.map((item) => {
              const isExpanded = expandedUuid === item.uuid;
              const isEditing = editingUuid === item.uuid;

              return (
                <React.Fragment key={item.uuid}>
                  <tr className="group">
                    <td className="px-4 py-3">
                      {item.category === "bug" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                          <Bug className="w-3 h-3" />
                          Bug
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                          <Lightbulb className="w-3 h-3" />
                          Feature
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setExpandedUuid(isExpanded ? null : item.uuid)} className="text-left">
                        <span className="text-sm font-medium text-content-primary hover:text-brand-primary transition-colors">
                          {item.title}
                        </span>
                        {item.screenshots.length > 0 && (
                          <span className="ml-2 inline-flex items-center gap-0.5 text-xs text-content-quaternary">
                            <ImageIcon className="w-3 h-3" />
                            {item.screenshots.length}
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-content-primary">{item.userName || "Unknown"}</div>
                      <div className="text-xs text-content-quaternary">{item.userEmail}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_COLORS[item.status] || STATUS_COLORS.open
                        }`}
                      >
                        {STATUS_LABELS[item.status] || item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-content-tertiary whitespace-nowrap">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setExpandedUuid(isExpanded ? null : item.uuid)}
                          className="px-2 py-1 text-xs bg-surface-secondary hover:bg-interactive-active text-content-secondary rounded-lg transition-colors"
                        >
                          {isExpanded ? "Collapse" : "Details"}
                        </button>
                        {!isEditing && (
                          <button
                            onClick={() => handleStartEdit(item)}
                            className="px-2 py-1 text-xs bg-surface-secondary hover:bg-interactive-active text-content-secondary rounded-lg transition-colors"
                          >
                            Update
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bg-surface-secondary/30">
                      <td colSpan={6} className="px-6 py-4 space-y-4">
                        {/* Description */}
                        <div>
                          <h4 className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-1">
                            Description
                          </h4>
                          <p className="text-sm text-content-secondary whitespace-pre-wrap">{item.description}</p>
                        </div>

                        {/* Page URL */}
                        {item.pageUrl && (
                          <div>
                            <h4 className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-1">
                              Page URL
                            </h4>
                            <p className="text-sm text-content-tertiary break-all">{item.pageUrl}</p>
                          </div>
                        )}

                        {/* Screenshots */}
                        {item.screenshots.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-2">
                              Screenshots
                            </h4>
                            <div className="flex gap-2 flex-wrap">
                              {item.screenshots.map((ss, idx) => (
                                <ScreenshotThumbnail
                                  key={ss.blobName}
                                  feedbackUuid={item.uuid}
                                  screenshot={ss}
                                  onClick={() =>
                                    setLightbox({
                                      feedbackUuid: item.uuid,
                                      screenshots: item.screenshots,
                                      index: idx,
                                    })
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Admin notes (read-only when not editing) */}
                        {item.adminNotes && !isEditing && (
                          <div>
                            <h4 className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-1">
                              Admin Notes
                            </h4>
                            <p className="text-sm text-content-secondary whitespace-pre-wrap">{item.adminNotes}</p>
                          </div>
                        )}

                        {/* Inline edit form */}
                        {isEditing && (
                          <div className="bg-surface-primary border border-border-secondary rounded-lg p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label
                                  htmlFor="feedback-status"
                                  className="block text-xs font-medium text-content-tertiary mb-1"
                                >
                                  Status
                                </label>
                                <select
                                  id="feedback-status"
                                  value={editStatus}
                                  onChange={(e) => setEditStatus(e.target.value)}
                                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                                >
                                  {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                      {STATUS_LABELS[s]}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div>
                              <label
                                htmlFor="feedback-admin-notes"
                                className="block text-xs font-medium text-content-tertiary mb-1"
                              >
                                Admin Notes
                              </label>
                              <textarea
                                id="feedback-admin-notes"
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                rows={3}
                                placeholder="Internal notes about this feedback..."
                                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSaveStatus(item.uuid)}
                                disabled={saving}
                                className="px-4 py-1.5 bg-brand-gradient text-white text-sm rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                              >
                                {saving ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin inline-block mr-1" />
                                    Saving...
                                  </>
                                ) : (
                                  "Save"
                                )}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-4 py-1.5 bg-surface-secondary hover:bg-interactive-active text-content-secondary text-sm rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {items.length === 0 && !loading && (
        <div className="text-center py-12">
          <MessageSquarePlus className="w-12 h-12 text-content-quaternary mx-auto mb-3" />
          <p className="text-content-tertiary">No feedback submitted yet</p>
        </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 bg-surface-secondary text-content-primary rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-interactive-active transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-content-tertiary">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages}
            className="px-3 py-1 bg-surface-secondary text-content-primary rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-interactive-active transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Screenshot Lightbox */}
      {lightbox && (
        <ScreenshotLightbox
          feedbackUuid={lightbox.feedbackUuid}
          screenshots={lightbox.screenshots}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
